/* eslint-disable radix */
/* eslint-disable no-unexpected-multiline */
/* eslint-disable import/no-cycle */
/* eslint-disable dot-notation */
import axios from 'axios';
import Cookies from 'universal-cookie';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { ui } from '@src/services';
import { reactLocalStorage } from '@src/utils/localStorage';

import { TOAST_TYPE_ERROR } from '@src/const/common';
import { checkIsServer } from '@src/utils/deprecatedCommon';
import { isProd } from '@src/utils/env';
import { onApiResponseSuccess, maybeServeFromCache } from '@src/api/offlineApiCache';

const OFFLINE_REJECT_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// 測試 server 白頁
const getGlobalFaultHeader = () => {
	if (checkIsServer() || isProd) return null;
	const search = window.location?.search;
	if (!search) return null;
	const fault = new URLSearchParams(search).get('fault_all');
	return fault ? { 'X-Internal-Fault-Inject': fault } : null;
};

// 測試打卡失敗
const getPunchPostFaultHeader = (method, url) => {
	if (checkIsServer() || isProd) return null;
	if ((method || '').toUpperCase() !== 'POST') return null;
	if (!url || !url.includes('punch-in-records')) return null;
	const search = window.location?.search;
	if (!search) return null;
	const fault = new URLSearchParams(search).get('fault');
	return fault ? { 'X-Internal-Fault-Inject': fault } : null;
};

const toApiLanguage = lng => {
	const lower = (lng || '').toLowerCase();
	if (lower.split('-')[0] === 'en') return 'en';
	return 'zh-TW';
};

const getLanguageHeader = () => {
	// eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
	const i18n = require('@src/i18n').default;
	return { 'Accept-Language': toApiLanguage(i18n?.language) };
};

// 延遲 require 避免循環依賴（store → reducers → services → api）
// eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
const getStore = () => require('@src/store').getClientStore?.();
// eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
const getSetToken = () => require('@src/store/actions/session').setToken;
// eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
const getLogoutCall = () => require('@src/store/actions/session').logoutCall;

const cookies = new Cookies();
let attemptedSessionToken = null;
let exchangePromise = null;
let exchangingSessionToken = null;
let isRefreshing = false;
let failedQueue = [];

const getLegacySessionToken = () => {
	const signedIn = reactLocalStorage.get('isSignedIn');
	const isJwtToken = reactLocalStorage.get('isJwtToken');
	if ((signedIn !== true && signedIn !== 'true') || isJwtToken === true || isJwtToken === 'true') return null;
	return reactLocalStorage.get('token') || cookies.get('token') || null;
};

const shouldExchangeSession = config => {
	// 離線或明確跳過認證處理的請求，不觸發換發。
	if (config.skipSessionExchange || config.skipAuthRefresh || !window.navigator.onLine) return null;
	// 只處理本站 API，並排除換發、refresh 與登出，避免遞迴或干擾既有流程。
	if (!config.url || /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(config.url)) return null;
	if (['user/session/exchange', 'user/refresh', 'user/logout'].includes(config.url)) return null;
	// 只換發目前請求實際使用的舊 session，不覆蓋自訂 Authorization。
	const sessionToken = getLegacySessionToken();
	if (!sessionToken || config.headers?.Authorization !== sessionToken) return null;
	// 同一次頁面載入，每個舊 session 最多嘗試一次。
	const canExchange = exchangingSessionToken === sessionToken || attemptedSessionToken !== sessionToken;
	return canExchange ? sessionToken : null;
};

const processQueue = (error, token = null) => {
	failedQueue.forEach(({ resolve, reject }) => {
		if (error) {
			reject(error);
		} else {
			resolve(token);
		}
	});
	failedQueue = [];
};

// 只在瀏覽器端掛 interceptor；SSR 環境不需要 refresh 流程。
if (!checkIsServer()) {
	axios.interceptors.request.use(config => {
		const sessionToken = shouldExchangeSession(config);
		if (!sessionToken) return config;
		const store = getStore();
		if (!store) return config;

		if (exchangingSessionToken !== sessionToken) {
			attemptedSessionToken = sessionToken;
			exchangingSessionToken = sessionToken;
			const pending = axios.post('user/session/exchange', null, {
				headers: { Authorization: sessionToken },
				skipSessionExchange: true,
				skipAuthRefresh: true,
			}).then(res => {
				const token = res?.data?.access_token;
				if (!token || getLegacySessionToken() !== sessionToken) return null;
				store.dispatch(getSetToken()({ token, isJwtToken: true }));
				return token;
			}).catch(() => null);
			exchangePromise = pending;
			pending.finally(() => {
				if (exchangePromise === pending) {
					exchangePromise = null;
					exchangingSessionToken = null;
				}
			});
		}

		return exchangePromise.then(token => {
			if (!token) return config;
			return { ...config, headers: { ...config.headers, Authorization: `Bearer ${token}` } };
		});
	});

	axios.interceptors.response.use(
		response => {
			// 取代 SW：白名單 GET 的 2xx 寫進 IndexedDB 快取，並把 punch-in-info 的 stale flag 清掉
			onApiResponseSuccess(response);
			return response;
		},
		error => {
			// 取代 SW 的 networkFirst fallback：白名單 GET 撞 5xx / timeout，改用 IndexedDB 快取回應
			// 	命中 → resolve synthetic response；
			// 	miss / 不適用 → 回 null 走原流程
			const offlineResponse = maybeServeFromCache(error);
			if (offlineResponse) return offlineResponse;

			const originalRequest = error.config || {};
			const isJwtToken = reactLocalStorage.get('isJwtToken');
			const jwtEnabled = isJwtToken === true || isJwtToken === 'true';

			// refresh 本身失敗 / 已重送過 / 明確標示不 refresh（如登出 API）/ 非 JWT 登入 / 非 401，一律不再嘗試 refresh。
			if (
				error.response?.status !== 401 ||
				originalRequest.isRefresh ||
				originalRequest.isRetry ||
				originalRequest.skipAuthRefresh ||
				!jwtEnabled
			) {
				return Promise.reject(error);
			}

			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then(token => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					return axios(originalRequest);
				});
			}

			originalRequest.isRetry = true;
			isRefreshing = true;

			return new Promise((resolve, reject) => {
				axios.post('user/refresh', null, {
					headers: { Authorization: '' },
					isRefresh: true,
				}).then(res => {
					const token = res?.data?.access_token;
					const store = getStore();

					if (store) {
						store.dispatch(getSetToken()({ token, isJwtToken: true }));
					}

					originalRequest.headers.Authorization = `Bearer ${token}`;
					processQueue(null, token);
					resolve(axios(originalRequest));
				}).catch(err => {
					processQueue(err, null);
					// 區分失敗原因，只有「真的 auth 失效」(401/403) 才 logout
					const status = err?.response?.status;
					const isAuthFailure = status === 401 || status === 403;
					if (isAuthFailure) {
						const store = getStore();
						if (store) {
							store.dispatch(getLogoutCall()());
						}
					}
					reject(err);
				}).finally(() => {
					isRefreshing = false;
				});
			});
		},
	);
}

// /hr 主頁有離線快取（src/db/apiResponseCache.ts）+ stale-mode UI 自己降級顯示，5xx / timeout toast 反而誤導使用者。
// 子頁不在 scope：retry scheduler 的 5xx / timeout 由 caller 自己帶 suppressErrorToast=true 處理，
// 不再用 path-level 一律吞，避免吞掉子頁本來該顯示的 API error toast。
const isOfflineFriendlyPath = () => (
	typeof window !== 'undefined' &&
	(window.location.pathname === '/hr' || window.location.pathname === '/hr/')
);

// axios.interceptors.request.use(
//   function (config) {
//     // Do something before request is sent

//     setTimeout(() => {
//       console.log(axios.defaults.headers.common);
//       console.log(config.headers.common);
//     }, 3000);

//     return config;
//   },
//   function (error) {
//     // Do something with request error
//     return Promise.reject(error);
//   }
// );

const handleCommonError = debounce(err => {
	const { message, response, code } = err;

	console.error('handleCommonError', code, message);

	if (isOfflineFriendlyPath()) return;

	if (code === 'ECONNABORTED') {
		ui.toastShow({ message: 'common:message:server_error', type: TOAST_TYPE_ERROR });
	} else if (message === 'Network Error') {
		ui.toastShow({ message: 'common:message:network_error', type: TOAST_TYPE_ERROR });
	} else if (response) {
		const { status } = response;

		switch (status) {
		case 500:
			ui.toastShow({ message: 'common:message:server_error', type: TOAST_TYPE_ERROR });
			break;
		case 502:
			ui.toastShow({ message: 'common:message:server_error', type: TOAST_TYPE_ERROR });
			break;
		case 503:
			ui.toastShow({ message: 'common:message:server_error', type: TOAST_TYPE_ERROR });
			break;
		default:
			break;
		}
	}
}, 300);

const request = (method, url, data, headers, otherConfigs = {}) => {
	// 離線時只擋寫入類請求；GET / HEAD 放行讓 axios interceptor 的 API 回應快取（src/api/offlineApiCache.ts）接手
	if (!checkIsServer() && !window.navigator.onLine && OFFLINE_REJECT_METHODS.includes((method || '').toUpperCase())) {
		if (!isOfflineFriendlyPath()) {
			ui.toastShow({ message: 'common:message:network_error', type: TOAST_TYPE_ERROR });
		}

		return Promise.reject();
	}

	const { hasPagination, ssrHeaders = {}, customHeader = {}, suppressErrorToast, ...remainingConfigs } = otherConfigs;
	// 語系放最前面，caller 自帶的 Accept-Language 仍可覆寫
	let newHeaders = { ...getLanguageHeader(), ...headers };

	if (!isEmpty(ssrHeaders) || !isEmpty(customHeader)) {
		newHeaders = {
			...newHeaders,
			...ssrHeaders,
			...customHeader,
			Authorization: customHeader?.Authorization || headers?.Authorization || ssrHeaders?.Authorization || '',
		};
	}

	const globalFaultHeader = getGlobalFaultHeader();
	if (globalFaultHeader) {
		newHeaders = { ...newHeaders, ...globalFaultHeader };
	}

	const punchPostFaultHeader = getPunchPostFaultHeader(method, url);
	if (punchPostFaultHeader) {
		newHeaders = { ...newHeaders, ...punchPostFaultHeader };
	}

	const param = {
		url,
		method,
		headers: newHeaders,
		data: method !== 'GET' ? data : null,
		params: method === 'GET' ? data : null,
		timeout: 15 * 1000,
		...remainingConfigs,
	};

	return axios(param)
		.then(res => {
			if (hasPagination) {
				const nextId = res.headers['x-next-id'];
				const totalCount = parseInt(res.headers['x-total-count'], 10);
				const pagination = {
					totalCount,
					...nextId ? { nextId } : {},
				};

				return Promise.resolve({
					data: res.data,
					pagination,
				});
			}

			return Promise.resolve(res.data);
		})
		['catch'](err => {
			if (axios.isCancel(err)) {
				// 被取消請求
			}

			if (!suppressErrorToast) handleCommonError(err);

			return Promise.reject(err);
		});
};

export default request;
